// The markers on the ruler (spec markers.md): a flag at each one's time, in its colour. A click
// moves the playhead to it and a drag moves it; a double-click or a right-click opens its panel:
// its label, its colour, its time and Delete marker.

import { Button, Popover, Text, TextInput } from "@mantine/core";
import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

import { formatTimecode } from "./time";
import { MARKER_COLORS, type Marker, type TimelineEdit } from "./timeline";
import classes from "./markers.module.css";

/** Live's MCP's names of `MARKER_COLORS`, in their order. */
const COLOR_NAMES = ["Blue", "Red", "Green", "Orange", "Purple", "Gold"] as const;
/** A press that travels less than this is a click: a seek, not a drag. */
const DRAG_PX = 3;

/**
 * Keeps the panel's presses and moves to itself. Its portal moves its DOM, but React carries them up
 * its own tree, to the ruler's seek and the tracks' preview axis; their default (a field's focus) stays.
 */
const keepInside = (event: { stopPropagation: () => void }) => event.stopPropagation();

export interface MarkerFlagsProps {
  markers: readonly Marker[];
  /** Where a time lies on the ruler, and the time under a pointer (`Tracks`' one `timeAt`). */
  px: (ms: number) => number;
  timeAt: (clientX: number) => number;
  readOnly: boolean;
  onSeek: (ms: number) => void;
  onEdit: (edit: TimelineEdit) => void;
}

export function MarkerFlags(props: MarkerFlagsProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <>
      {props.markers.map((marker) => (
        <Flag
          key={marker.id}
          {...props}
          marker={marker}
          open={openId === marker.id}
          onOpen={(open) => setOpenId(open ? marker.id : null)}
        />
      ))}
    </>
  );
}

function Flag(props: MarkerFlagsProps & { marker: Marker; open: boolean; onOpen: (open: boolean) => void }) {
  const { marker, px, timeAt, readOnly, onSeek, onEdit, open, onOpen } = props;
  const at = formatTimecode(marker.atMs, true);
  // A press that became a drag does not also seek when it ends.
  const dragged = useRef(false);
  const press = (event: PointerEvent<HTMLButtonElement>) => {
    // The ruler's own press seeks to the pointer; a flag's is its own.
    event.stopPropagation();
    dragged.current = false;
    if (event.button !== 0 || readOnly) return;
    const fromX = event.clientX;
    const move = (moved: MouseEvent) => {
      if (!dragged.current && Math.abs(moved.clientX - fromX) < DRAG_PX) return;
      dragged.current = true;
      // Each move is one edit; the history makes the drag one step.
      onEdit({ type: "setMarker", id: marker.id, patch: { atMs: timeAt(moved.clientX) } });
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
  };
  const openPanel = () => {
    if (!readOnly) onOpen(true);
  };
  return (
    <Popover opened={open} onChange={onOpen} position="bottom" shadow="md" withinPortal>
      <Popover.Target>
        <button
          type="button"
          className={classes.flag}
          style={{ left: px(marker.atMs), color: marker.color }}
          title={`${marker.label} · ${at}`}
          aria-label={`${marker.label}, a marker at ${at}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          data-marker={marker.id}
          onPointerDown={press}
          onClick={() => {
            if (dragged.current) dragged.current = false;
            else onSeek(marker.atMs);
          }}
          onDoubleClick={openPanel}
          onContextMenu={(event) => {
            event.preventDefault();
            event.stopPropagation();
            openPanel();
          }}
        />
      </Popover.Target>
      <Popover.Dropdown aria-label={`Marker ${marker.label}`} onPointerDown={keepInside} onPointerMove={keepInside}>
        <MarkerPanel marker={marker} at={at} onEdit={onEdit} onClose={() => onOpen(false)} />
      </Popover.Dropdown>
    </Popover>
  );
}

/** A marker's panel: its label (kept on Enter or on leaving the field), its colour, its time, Delete. */
function MarkerPanel(props: { marker: Marker; at: string; onEdit: (edit: TimelineEdit) => void; onClose: () => void }) {
  const { marker, at, onEdit, onClose } = props;
  const set = (patch: { label?: string; color?: string }) => onEdit({ type: "setMarker", id: marker.id, patch });
  const commit = (value: string) => {
    if (value.trim() !== "" && value !== marker.label) set({ label: value });
  };
  return (
    <div className={classes.panel}>
      <TextInput
        key={marker.label}
        label="Label"
        size="xs"
        defaultValue={marker.label}
        maxLength={100}
        onBlur={(event) => commit(event.currentTarget.value)}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (event.key === "Enter") commit(event.currentTarget.value);
        }}
      />
      <fieldset className={classes.swatches}>
        <legend className={classes.legend}>Color</legend>
        {MARKER_COLORS.map((hex, index) => (
          <button
            key={hex}
            type="button"
            className={classes.swatch}
            style={{ color: hex }}
            aria-label={COLOR_NAMES[index]}
            aria-pressed={marker.color === hex}
            onClick={() => set({ color: hex })}
          />
        ))}
      </fieldset>
      <Text size="xs" c="dimmed">
        At {at}
      </Text>
      <Button
        variant="subtle"
        color="red"
        size="xs"
        onClick={() => {
          onEdit({ type: "removeMarker", id: marker.id });
          onClose();
        }}
      >
        Delete marker
      </Button>
    </div>
  );
}
